// Page-section slot registry (#253).
//
// A feature module that owns a section another module's PAGE embeds (the
// signed-in employee's income list on /account/my-incomes, the attendance table
// on /account) registers it here; the page renders it by id with
// <Slot id="work-day.list" />. Neither side imports the other: this file is
// neutral (src/lib may not import a module, `no-shared-to-module`).
//
// This is the same mechanism as `dashboard-widgets.tsx`, kept apart on purpose:
// that registry is named for, and styled for, Dashboard cards (its fallback is
// a Card). A page section is not a dashboard widget and its fallback differs.
//
// Registration is lazy by construction: `load` is a dynamic import and the
// registry wraps it in React.lazy, so the owner's code and locales stay out of
// the entry chunk. Call `registerSlot` at module scope from the owner's
// `index.tsx` (the way `./locales/register` is imported).
//
// Props are passed through untyped (as for dashboard widgets): the owner's
// component keeps its own prop types, the slot cannot check them.
import {
    lazy,
    Suspense,
    type ComponentProps,
    type ComponentType,
    type LazyExoticComponent,
    type ReactNode,
} from 'react'
import { Skeleton } from '@vnetwork/datacentral-ui/primitives'

type AnyComponent = ComponentType<any>

interface SlotRegistration {
    /** `<module>.<section>` in kebab-case, e.g. `work-day.list`. */
    id: string
    /** Dynamic import of the component: `() => import('./components/X')`. */
    load: () => Promise<{ default: AnyComponent }>
}

const registry = new Map<string, LazyExoticComponent<AnyComponent>>()

/** Register a section under `id`. Registering the same id again replaces it. */
export const registerSlot = ({ id, load }: SlotRegistration): void => {
    registry.set(id, lazy(load))
}

interface SlotProps {
    id: string
    /** Props passed to the registered component. */
    props?: ComponentProps<AnyComponent>
    /** Shown while the section loads (default: a 200px skeleton). */
    fallback?: ReactNode
}

/** Render the section registered under `id` (nothing if none is). */
export const Slot = ({ id, props, fallback }: SlotProps) => {
    const Section = registry.get(id)
    if (!Section) {
        if (import.meta.env.DEV) console.warn(`Slot "${id}" is not registered`)
        return null
    }
    return (
        <Suspense
            fallback={fallback ?? <Skeleton className="h-[200px] w-full" />}
        >
            <Section {...props} />
        </Suspense>
    )
}
